import React, { useState, useCallback } from 'react'
import { useMutation, queryCache } from 'react-query'
import styled from 'styled-components'
import moment from 'moment'

import ReportsForm from './ReportsForm'
import { downloadFileFromBlob } from '../../utils/download'
import ReportsEditForm from './ReportsEditForm'
import { useNotifications } from '../../containers/Notifications'
import { useWithAuth } from '../App'
import Button from '../../components/Button'
import Heading from '../../components/Heading'

import { Reports, statusLabels, stateImages, ModalStateProps } from './utils'
import { ID } from '../../types/id'
import colors from '../../styles/colors'

import { MailModal, ReportErrorModal } from './MailModal'
import Table from '../../components/Table'
import FlexBox from '../../components/Flexbox'
// @ts-ignore @todo add apprioprate declaration file
import copyIcon from '../../images/copy.svg'
// @ts-ignore @todo add apprioprate declaration file
import envelopeIcon from '../../images/envelopeIcon.svg'
// @ts-ignore @todo add apprioprate declaration file
import refreshIcon from '../../images/refreshIcon.svg'
// @ts-ignore @todo add apprioprate declaration file
import addIcon from '../../images/addIcon.svg'
// @ts-ignore @todo add apprioprate declaration file
import closeIcon from '../../images/closeIcon-red.svg'
// @ts-ignore @todo add apprioprate declaration file
import editIcon from '../../images/grayEditIcon.svg'
// @ts-ignore @todo add apprioprate declaration file
import saveIcon from '../../images/grayDownloadIcon.svg'
// @ts-ignore @todo add apprioprate declaration file
import deleteIcon from '../../images/redDeleteIcon.svg'
import { navigate } from 'gatsby'

type Props = {
  onClose: () => void
  handleRefresh: () => void
  reports: Reports[]
  code: string
  inquired: boolean
  diagnosisStatus: string
  patient: any
  id: ID
}

type StateEdit = {
  reportId: ID | null
}
const normalizeDate = (date) => moment(date).format('YYYY-MM-DD')

export function ReportsPanel({
  id,
  reports,
  diagnosisStatus,
  inquired,
  code,
  patient,
  handleRefresh,
}: Props) {
  const [activeEditForm, setActiveEditForm] = useState<StateEdit>({
    reportId: null,
  })
  const [activeGenerateForm, setActiveGenerateForm] = useState<boolean>(false)
  const [modalState, setModalState] = useState<ModalStateProps>({
    open: false,
    reportId: null,
    reportName: null,
    reportErrorDescription: '',
  })
  const endpoint = `/report/rest/examination/${id}/report`
  const withAuth = useWithAuth()
  const { notify } = useNotifications()

  const handleGenerateState = () => {
    setActiveGenerateForm(!activeGenerateForm)
    setActiveEditForm({ reportId: null })
  }

  const handleEditState = (value) => {
    activeEditForm.reportId === null
      ? setActiveEditForm({ reportId: value })
      : setActiveEditForm({ reportId: null })
    setActiveGenerateForm(false)
  }

  const [handleDelete] = useMutation<any, ID>(
    (reportId) =>
      withAuth(`${endpoint}/${reportId}`, {
        method: 'DELETE',
      })
        .then((res) => {
          if ('fieldErrors' in res) {
            // @todo notifications
          } else {
            queryCache.refetchQueries(`reports-${id}`)
            notify('Dodano zlecenie usunięcia raportu', 'success')
          }
        })
        .catch(console.log),

    {}
  )

  const handleDownload = useCallback((report) => {
    withAuth(
      `${endpoint}/${report.id}/download/${report.name}`,
      {
        method: 'GET',
        responseType: 'blob',
      },
      true
    ).then((res) => downloadFileFromBlob(res, `${report.name}.pdf`))
  }, [])

  const handleMailSending = useCallback(
    (vals) => {
      const sendMail = async () => {
        const result = await withAuth(
          `/report/rest/examination/${id}/report/${modalState.reportId}/send`,
          {
            method: 'POST',
            payload: {
              email: vals.email,
              filename: `${modalState.reportName}.pdf`,
            },
          }
        )
        result
          ? notify('Zarejestrowano zlecenie wysyłki', 'success')
          : notify(
              'Nie udało zarejestrować zlecenia wysyłki, spróbuj ponownie',
              'error'
            )
        setModalState({
          open: false,
          reportId: null,
          reportName: null,
          reportErrorDescription: '',
        })
      }
      sendMail()
    },
    [id, modalState.reportId, withAuth, notify]
  )

  return (
    <Wrapper
      shouldOverflow={activeGenerateForm || activeEditForm.reportId !== null}
    >
      <Heading title="Raporty">
        <Button
          link={null}
          icon={closeIcon}
          label="Powrót"
          onClick={() =>
            navigate(`/app/patients/${patient.id}`, {
              state: {
                tabIndex: 1,
              },
            })
          }
          color="white"
          altTag=""
          srText=""
          imgAriaHidden={true}
        />
        <Button
          link={null}
          marginLeft={true}
          icon={refreshIcon}
          label="Odśwież"
          onClick={() => handleRefresh()}
          color="white"
          altTag=""
          srText=""
          imgAriaHidden={true}
        />
        <Button
          link={null}
          marginLeft={true}
          icon={addIcon}
          label="Dodaj raport"
          onClick={() => handleGenerateState()}
          altTag=""
          srText=""
          imgAriaHidden={true}
        />
      </Heading>
      <div>
        <div>
          <Table
            defaultPageSize={0}
            data={reports}
            getLoadingProps={() => ({
              'aria-hidden': true,
              role: 'none',
            })}
            getTheadTrProps={() => ({
              style: { display: 'flex', alignItems: 'center' },
            })}
            columns={[
              {
                Header: 'Numer',
                id: 'id',
                sortable: false,
                accessor: ({ id }) => id,
                getProps: () => ({
                  role: undefined,
                }),
              },

              {
                Header: 'Nazwa robocza',
                sortable: false,
                id: 'name',
                accessor: ({ name }) => name,
                getProps: () => ({
                  role: undefined,
                }),
              },

              {
                Header: 'Zlecono',
                id: 'requestDate',
                sortable: false,
                accessor: ({ requestDate }) => normalizeDate(requestDate),
                getProps: () => ({
                  role: undefined,
                }),
              },
              {
                Header: 'Wygenerowano',
                id: 'generationDate',
                sortable: false,
                accessor: ({ generationDate }) =>
                  generationDate !== null
                    ? normalizeDate(generationDate)
                    : null,
                getProps: () => ({
                  role: undefined,
                }),
              },

              {
                Header: 'Status',
                id: 'state',
                sortable: false,
                Cell: ({ original }) => (
                  <>
                    <Img
                      src={stateImages[original.state]}
                      alt=""
                      aria-hidden="true"
                    />
                    <span style={{ marginLeft: '5px' }}>
                      {statusLabels[original.state]}
                    </span>
                  </>
                ),
              },
              {
                Header: 'Rozmiar',
                id: 'size',
                sortable: false,
                accessor: ({ size }) =>
                  size !== null ? `${Math.round(size / 1024)}kb` : null,
                getProps: () => ({
                  role: undefined,
                }),
              },
              {
                Header: 'Akcje',
                sortable: false,
                width: 340,
                Cell: ({ original }) => (
                  <FlexBox justify="space-between">
                    <IconButton
                      type="button"
                      disabled={
                        original.state === 'GENERATING' ||
                        original.state === 'REQUEST'
                      }
                      onClick={() => handleEditState(original.id)}
                    >
                      Odnów
                      <Img alt="" src={editIcon} aria-hidden="true" />
                    </IconButton>
                    {original.state !== 'ERROR' ? (
                      <>
                        <IconButton
                          type="button"
                          disabled={original.state !== 'DONE'}
                          onClick={() => handleDownload(original)}
                        >
                          Pobierz
                          <Img alt="" src={saveIcon} aria-hidden="true" />
                        </IconButton>
                        <IconButton
                          type="button"
                          disabled={original.state !== 'DONE'}
                          onClick={() =>
                            setModalState({
                              open: true,
                              reportId: original.id,
                              reportName: original.name,
                              reportErrorDescription: '',
                            })
                          }
                        >
                          Wyślij
                          <Img alt="" src={envelopeIcon} aria-hidden="true" />
                        </IconButton>
                      </>
                    ) : (
                      <IconButton
                        type="button"
                        onClick={() =>
                          setModalState({
                            open: true,
                            reportId: original.id,
                            reportName: original.name,
                            reportErrorDescription: original.errorDescription,
                          })
                        }
                      >
                        Pokaż błąd
                        <Img alt="" src={copyIcon} aria-hidden="true" />
                      </IconButton>
                    )}
                    <IconButton
                      type="button"
                      disabled={original.state === 'GENERATING'}
                      onClick={() => handleDelete(original.id)}
                    >
                      Usuń
                      <Img alt="" src={deleteIcon} aria-hidden="true" />
                    </IconButton>
                  </FlexBox>
                ),
              },
            ]}
          />
        </div>
        {activeGenerateForm ? (
          <ReportsForm
            patientFirstName={patient.person.firstName}
            patientLastName={patient.person.lastName}
            inquired={inquired}
            code={code}
            diagnosisStatus={diagnosisStatus}
            id={id}
            onClose={() => setActiveGenerateForm(false)}
          ></ReportsForm>
        ) : null}
        {activeEditForm.reportId !== null ? (
          <ReportsEditForm
            onClose={() => setActiveEditForm({ reportId: null })}
            reportId={activeEditForm.reportId}
            id={id}
            name={
              reports.find((report) => report.id === activeEditForm.reportId)
                ?.name
            }
            resultInterpretation={
              reports.find((report) => report.id === activeEditForm.reportId)
                ?.resultInterpretation
            }
          ></ReportsEditForm>
        ) : null}
      </div>
      {modalState.reportErrorDescription === '' ? (
        <MailModal
          isOpen={modalState.open}
          onClose={() => {
            setModalState({
              open: false,
              reportId: null,
              reportName: null,
              reportErrorDescription: '',
            })
          }}
          onSubmit={handleMailSending}
        />
      ) : (
        <ReportErrorModal
          isOpen={modalState.open}
          message={modalState.reportErrorDescription}
          onClose={() => {
            setModalState({
              open: false,
              reportId: null,
              reportName: null,
              reportErrorDescription: '',
            })
          }}
        />
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  ${(props) => props.shouldOverflow && 'overflow: hidden'}
`

const Img = styled.img`
  margin-bottom: 0;
`

const IconButton = styled.button`
  display: flex;
  margin: 0;
  background: transparent;
  border: 1px solid ${colors.light_black};
  &:hover {
    ${(props) => !props.disabled && `border: 1px solid ${colors.mantis_green};`}
  }
  &.danger {
    &:hover {
      border: 1px solid ${colors.red};
    }
  }
  align-items: center;
  cursor: ${(props) => !props.disabled && `pointer;`};
  height: auto;
  border-radius: 5px;
  > * {
    padding: 0 0 0 3px;
  }
  ${Img} {
    opacity: ${(props) => props.disabled && `50%`};
  }
`
